import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, AlertCircle } from "lucide-react";
import { StarsBackground, DISCORD_URL } from "@/components/StarsBackground";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [attempts, setAttempts] = useState(0);
  const [info, setInfo] = useState(null);

  const poll = useCallback(
    async (count) => {
      if (count >= 7) {
        setState("error");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setAttempts(count + 1);
        setTimeout(() => poll(count + 1), 2000);
      } catch (e) {
        setState("error");
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6" data-testid="checkout-success-page">
      <StarsBackground />
      <div className="relative z-10 max-w-lg w-full rv-card rounded-xl p-10 text-center">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin" size={48} style={{ color: "var(--rv-red)" }} />
            <h1 className="font-display font-black uppercase text-3xl mt-6">Confirming payment…</h1>
            <p className="text-white/60 mt-3 text-sm">Hang tight, this only takes a sec. ({attempts}/7)</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto" size={56} style={{ color: "#22c55e" }} />
            <h1 className="font-display font-black uppercase text-3xl mt-6">{"You're in."}</h1>
            <p className="text-white/70 mt-3 text-base">
              Your purchase is confirmed. Jump into the Discord to grab your files and any setup help.
            </p>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-8 justify-center w-full"
              data-testid="success-discord-btn"
            >
              Open Discord
            </a>
            <Link to="/" className="text-xs uppercase tracking-widest text-white/45 hover:text-white mt-6 inline-block">
              ← Back home
            </Link>
          </>
        )}
        {state === "expired" && (
          <>
            <AlertCircle className="mx-auto" size={48} style={{ color: "var(--rv-gold)" }} />
            <h1 className="font-display font-black uppercase text-3xl mt-6">Session expired</h1>
            <p className="text-white/60 mt-3 text-sm">Your checkout session timed out. Head back and try again.</p>
            <Link to="/#pricing" className="btn-primary mt-8 justify-center w-full">Back to pricing</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto" size={48} style={{ color: "var(--rv-red)" }} />
            <h1 className="font-display font-black uppercase text-3xl mt-6">Something went wrong</h1>
            <p className="text-white/60 mt-3 text-sm">
              {"We couldn't verify the payment status. If you were charged, ping us in Discord and we'll fix it."}
            </p>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-8 justify-center w-full"
            >
              Open Discord
            </a>
          </>
        )}
      </div>
    </div>
  );
}
